import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Badge,
    Popconfirm,
    Tooltip,
    Tabs,
    InputNumber,
    Switch,
    Descriptions,
    Dropdown,
    Drawer,
    Empty
} from 'antd';
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    SettingOutlined,
    KeyOutlined,
    ControlOutlined,
    MoreOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    StopOutlined,
    CheckCircleOutlined,
    SwapOutlined,
    SyncOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    CloudDownloadOutlined,
    CloudUploadOutlined
} from '@ant-design/icons';
import AdminTransferAction from '../../components/AdminTransferAction';
import { useNavigate } from 'react-router-dom';
import hostSharedService from '../../../shared/services/hostSharedService';
import hostSharedAdminService from '../../services/hostSharedAdminService';
import OffsiteBackupSettings from './OffsiteBackupSettings';

import { adminPath } from '../../../shared/config/paths';

const { Option } = Select;
const { TabPane } = Tabs;

const Vhosts = () => {
    const navigate = useNavigate();
    const [vhosts, setVhosts] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [quotaModalVisible, setQuotaModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [editingVhost, setEditingVhost] = useState(null);
    const [selectedVhost, setSelectedVhost] = useState(null);
    const [ownerModalVisible, setOwnerModalVisible] = useState(false);
    const [extendModalVisible, setExtendModalVisible] = useState(false);
    const [logsDrawerVisible, setLogsDrawerVisible] = useState(false);
    const [logsData, setLogsData] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [backupsDrawerVisible, setBackupsDrawerVisible] = useState(false);
    const [backupsData, setBackupsData] = useState([]);
    const [backupsLoading, setBackupsLoading] = useState(false);
    const [form] = Form.useForm();
    const [quotaForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [ownerForm] = Form.useForm();
    const [extendForm] = Form.useForm();

    useEffect(() => {
        loadVhosts();
        loadNodes();
        loadUsers();
    }, []);

    const loadVhosts = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getAdminVhosts();
            if (response.success) {
                const vhostsList = response.data?.list || response.data || [];
                setVhosts(Array.isArray(vhostsList) ? vhostsList : []);
            } else {
                message.error(response.error || '加载虚拟主机列表失败');
            }
        } catch (error) {
            message.error('加载虚拟主机列表失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadNodes = async () => {
        try {
            const response = await hostSharedService.getNodes();
            if (response.success) {
                const nodesList = response.data?.list || response.data || [];
                setNodes(Array.isArray(nodesList) ? nodesList : []);
            }
        } catch (error) {
            console.error('加载节点列表失败:', error);
        }
    };

    const loadUsers = async (keyword = '') => {
        try {
            const response = await hostSharedService.getUsers(keyword);
            if (response.success) {
                const usersList = response.data || [];
                setUsers(Array.isArray(usersList) ? usersList : []);
            }
        } catch (error) {
            console.error('加载用户列表失败:', error);
        }
    };

    const generateRandomName = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let name = '';
        for (let i = 0; i < 10; i++) name += chars.charAt(Math.floor(Math.random() * chars.length));
        return name;
    };

    const generateRandomPassword = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';
        let pwd = '';
        for (let i = 0; i < 16; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
        return pwd;
    };

    const handleCreate = () => {
        setEditingVhost(null);
        form.resetFields();
        form.setFieldsValue({
            name: generateRandomName(),
            passwd: generateRandomPassword(),
        });
        setModalVisible(true);
    };

    const handleViewDetail = async (record) => {
        try {
            const response = await hostSharedService.getAdminVhost(record.id);
            if (response.success) {
                setSelectedVhost(response.data);
                setDetailModalVisible(true);
            } else {
                message.error(response.error || '加载详情失败');
            }
        } catch (error) {
            message.error('加载详情失败: ' + error.message);
        }
    };

    const handleEditQuota = async (record) => {
        try {
            const response = await hostSharedService.getAdminVhost(record.id);
            if (response.success && response.data.quota) {
                setSelectedVhost(response.data);
                quotaForm.setFieldsValue(response.data.quota);
                setQuotaModalVisible(true);
            } else {
                message.error('加载配额信息失败');
            }
        } catch (error) {
            message.error('加载配额信息失败: ' + error.message);
        }
    };

    const handleChangePassword = (record) => {
        setSelectedVhost(record);
        passwordForm.resetFields();
        setPasswordModalVisible(true);
    };

    const handleManage = (record) => {
        navigate(adminPath(`/host-shared/vhosts/${record.id}`));
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const response = await hostSharedService.createAdminVhost(values);

            if (response.success) {
                message.success('虚拟主机创建成功');
                setModalVisible(false);
                loadVhosts();
            } else {
                message.error(response.error || '创建失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写');
            } else {
                message.error('创建失败: ' + error.message);
            }
        }
    };

    const handleQuotaSubmit = async () => {
        try {
            const values = await quotaForm.validateFields();

            const response = await hostSharedService.updateVhostQuota(selectedVhost.id, values);

            if (response.success) {
                message.success('配额更新成功');
                setQuotaModalVisible(false);
                loadVhosts();
            } else {
                message.error(response.error || '更新失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写');
            } else {
                message.error('更新失败: ' + error.message);
            }
        }
    };

    const handlePasswordSubmit = async () => {
        try {
            const values = await passwordForm.validateFields();

            const response = await hostSharedService.changeAdminVhostPassword(selectedVhost.id, values.password);

            if (response.success) {
                message.success('密码修改成功');
                setPasswordModalVisible(false);
            } else {
                message.error(response.error || '修改失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写');
            } else {
                message.error('修改失败: ' + error.message);
            }
        }
    };

    // ==========================================
    // 生命周期管理
    // ==========================================

    const runLifecycle = async (fn, successMsg) => {
        try {
            const response = await fn();
            if (response.success) {
                message.success(successMsg);
                loadVhosts();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败: ' + error.message);
        }
    };

    const handleSuspend = (record) => {
        Modal.confirm({
            title: `暂停虚拟主机 - ${record.name}`,
            content: '暂停后该虚拟主机将无法访问，可随时恢复。',
            okText: '确定暂停',
            cancelText: '取消',
            onOk: () => runLifecycle(() => hostSharedService.suspendVhost(record.id), '虚拟主机已暂停'),
        });
    };

    const handleResume = (record) => {
        runLifecycle(() => hostSharedService.resumeVhost(record.id), '虚拟主机已恢复');
    };

    const handleActivate = (record) => {
        runLifecycle(() => hostSharedService.activateVhost(record.id), '虚拟主机已激活');
    };

    const handleForceSync = (record) => {
        runLifecycle(() => hostSharedService.forceSyncVhost(record.id), '已触发强制同步');
    };

    const handleTerminate = (record) => {
        Modal.confirm({
            title: `终止虚拟主机 - ${record.name}`,
            content: '终止将停止服务。此操作不可逆，请谨慎操作。',
            okText: '确定终止',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: () => runLifecycle(() => hostSharedService.terminateVhost(record.id, false), '虚拟主机已终止'),
        });
    };

    const handleChangeOwner = (record) => {
        setSelectedVhost(record);
        ownerForm.resetFields();
        setOwnerModalVisible(true);
    };

    const handleOwnerSubmit = async () => {
        try {
            const values = await ownerForm.validateFields();
            const response = await hostSharedService.changeVhostOwner(selectedVhost.id, values.new_user_id);
            if (response.success) {
                message.success('归属已变更');
                setOwnerModalVisible(false);
                loadVhosts();
            } else {
                message.error(response.error || '变更失败');
            }
        } catch (error) {
            if (!error.errorFields) message.error('变更失败: ' + error.message);
        }
    };

    const handleExtend = (record) => {
        setSelectedVhost(record);
        extendForm.resetFields();
        extendForm.setFieldsValue({ days: 30, free: false });
        setExtendModalVisible(true);
    };

    const handleExtendSubmit = async () => {
        try {
            const values = await extendForm.validateFields();
            const response = await hostSharedService.extendVhost(selectedVhost.id, values.days, values.free);
            if (response.success) {
                message.success('已延长到期时间');
                setExtendModalVisible(false);
                loadVhosts();
            } else {
                message.error(response.error || '续期失败');
            }
        } catch (error) {
            if (!error.errorFields) message.error('续期失败: ' + error.message);
        }
    };

    // ==========================================
    // 操作日志 / 备份
    // ==========================================

    const handleViewLogs = async (record) => {
        setSelectedVhost(record);
        setLogsDrawerVisible(true);
        setLogsLoading(true);
        try {
            const response = await hostSharedAdminService.getVhostLogs(record.id);
            if (response.success) {
                const list = response.data?.list || response.data || [];
                setLogsData(Array.isArray(list) ? list : []);
            } else {
                setLogsData([]);
                message.error(response.error || '加载操作日志失败');
            }
        } catch (error) {
            setLogsData([]);
            message.error('加载操作日志失败: ' + error.message);
        } finally {
            setLogsLoading(false);
        }
    };

    const loadBackups = async (record) => {
        setBackupsLoading(true);
        try {
            const response = await hostSharedAdminService.getVhostBackups(record.id);
            if (response.success) {
                const list = response.data?.list || response.data || [];
                setBackupsData(Array.isArray(list) ? list : []);
            } else {
                setBackupsData([]);
                message.error(response.error || '加载备份列表失败');
            }
        } catch (error) {
            setBackupsData([]);
            message.error('加载备份列表失败: ' + error.message);
        } finally {
            setBackupsLoading(false);
        }
    };

    const handleViewBackups = (record) => {
        setSelectedVhost(record);
        setBackupsDrawerVisible(true);
        loadBackups(record);
    };

    const handleCreateBackup = (type) => {
        if (!selectedVhost) return;
        Modal.confirm({
            title: `创建${type === 'db' ? '数据库' : '网站'}备份 - ${selectedVhost.name}`,
            content: '备份任务将在节点后台执行，完成后会出现在下方列表中。',
            okText: '开始备份',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const response = await hostSharedAdminService.createVhostBackup(selectedVhost.id, { type });
                    if (response.success) {
                        message.success('备份任务已启动');
                        loadBackups(selectedVhost);
                    } else {
                        message.error(response.error || '备份失败');
                    }
                } catch (error) {
                    message.error('备份失败: ' + error.message);
                }
            },
        });
    };

    const getLifecycleMenu = (record) => {
        const items = [];
        if (record.status === 'active') {
            items.push({ key: 'suspend', icon: <PauseCircleOutlined />, label: '暂停' });
        }
        if (record.status === 'suspended') {
            items.push({ key: 'resume', icon: <PlayCircleOutlined />, label: '恢复' });
        }
        if (record.status !== 'active') {
            items.push({ key: 'activate', icon: <CheckCircleOutlined />, label: '激活' });
        }
        items.push({ key: 'extend', icon: <ClockCircleOutlined />, label: '续期' });
        items.push({ key: 'force-sync', icon: <SyncOutlined />, label: '强制同步' });
        items.push({ type: 'divider' });
        items.push({ key: 'logs', icon: <FileTextOutlined />, label: '操作日志' });
        items.push({ key: 'backups', icon: <CloudDownloadOutlined />, label: '备份管理' });
        items.push({ type: 'divider' });
        items.push({ key: 'terminate', icon: <StopOutlined />, label: '终止', danger: true });
        return {
            items,
            onClick: ({ key }) => {
                switch (key) {
                    case 'suspend': return handleSuspend(record);
                    case 'resume': return handleResume(record);
                    case 'activate': return handleActivate(record);
                    case 'extend': return handleExtend(record);
                    case 'change-owner': return handleChangeOwner(record);
                    case 'force-sync': return handleForceSync(record);
                    case 'logs': return handleViewLogs(record);
                    case 'backups': return handleViewBackups(record);
                    case 'terminate': return handleTerminate(record);
                    default: return undefined;
                }
            },
        };
    };

    const handleDelete = async (id) => {
        try {
            const response = await hostSharedService.deleteAdminVhost(id);
            if (response.success) {
                message.success('虚拟主机已删除');
                loadVhosts();
            } else {
                message.error(response.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败: ' + error.message);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'active': { status: 'success', text: '运行中' },
            'suspended': { status: 'warning', text: '已暂停' },
            'expired': { status: 'error', text: '已过期' }
        };
        const config = statusMap[status] || { status: 'default', text: '未知' };
        return <Badge status={config.status} text={config.text} />;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 80,
        },
        {
            title: '虚拟主机名',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '用户ID',
            dataIndex: 'user_id',
            key: 'user_id',
        },
        {
            title: '节点',
            dataIndex: 'node_name',
            key: 'node_name',
            render: (text) => text || '-',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusBadge(status),
        },
        {
            title: '磁盘配额',
            key: 'disk_quota',
            render: (_, record) => record.quota ? `${record.quota.disk_quota} MB` : '-',
        },
        {
            title: '流量配额',
            key: 'bandwidth_quota',
            render: (_, record) => record.quota ? `${record.quota.bandwidth_quota} GB/月` : '-',
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 430,
            render: (_, record) => (
                <Space>
                    <AdminTransferAction type="host_shared" id={record.id} name={record.name} onDone={() => loadVhosts()}>
                        <Tooltip title="转移给其他账号"><Button type="link" size="small" icon={<SwapOutlined />} /></Tooltip>
                    </AdminTransferAction>
                    <Tooltip title="进入管理面板">
                        <Button
                            type="primary"
                            size="small"
                            icon={<ControlOutlined />}
                            onClick={() => handleManage(record)}
                        >
                            管理
                        </Button>
                    </Tooltip>
                    <Tooltip title="查看详情">
                        <Button
                            type="link"
                            icon={<InfoCircleOutlined />}
                            onClick={() => handleViewDetail(record)}
                        >
                            详情
                        </Button>
                    </Tooltip>
                    <Tooltip title="配额管理">
                        <Button
                            type="link"
                            icon={<SettingOutlined />}
                            onClick={() => handleEditQuota(record)}
                        >
                            配额
                        </Button>
                    </Tooltip>
                    <Tooltip title="修改密码">
                        <Button
                            type="link"
                            icon={<KeyOutlined />}
                            onClick={() => handleChangePassword(record)}
                        >
                            密码
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        title="确定要删除这个虚拟主机吗？"
                        description="删除后数据将无法恢复"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                    <Dropdown menu={getLifecycleMenu(record)} trigger={['click']}>
                        <Button type="link" icon={<MoreOutlined />}>
                            更多
                        </Button>
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card
                title="虚拟主机管理"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={loadVhosts}>
                            刷新
                        </Button>
                        <OffsiteBackupSettings />
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            创建虚拟主机
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={vhosts}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1400 }}
                />
            </Card>

            {/* 创建虚拟主机 */}
            <Modal
                title="创建虚拟主机"
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={800}
            >
                <Form form={form} layout="vertical">
                    <Tabs defaultActiveKey="basic">
                        <TabPane tab="基本信息" key="basic">
                            <Form.Item
                                label="用户ID"
                                name="user_id"
                                rules={[{ required: true, message: '请选择用户' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="请选择用户"
                                    optionFilterProp="children"
                                    onSearch={loadUsers}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {users.map(user => (
                                        <Option key={user.id} value={user.id}>
                                            {user.id} - {user.username} ({user.email})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="虚拟主机名"
                                name="name"
                                rules={[{ required: true, message: '请输入虚拟主机名' }]}
                                extra="只能包含小写字母、数字和下划线，已自动生成"
                            >
                                <Input placeholder="user001" addonAfter={
                                    <ReloadOutlined onClick={() => form.setFieldsValue({ name: generateRandomName() })} style={{ cursor: 'pointer' }} title="重新生成" />
                                } />
                            </Form.Item>

                            <Form.Item
                                label="密码"
                                name="passwd"
                                rules={[{ required: true, message: '请输入密码' }]}
                            >
                                <Input.Password placeholder="设置虚拟主机密码" addonAfter={
                                    <ReloadOutlined onClick={() => form.setFieldsValue({ passwd: generateRandomPassword() })} style={{ cursor: 'pointer' }} title="重新生成" />
                                } />
                            </Form.Item>

                            <Form.Item
                                label="选择节点"
                                name="node_id"
                                rules={[{ required: true, message: '请选择节点' }]}
                                extra="选择要创建虚拟主机的节点"
                            >
                                <Select placeholder="请选择节点">
                                    {nodes.filter(n => n.status === 'online').map(node => (
                                        <Option key={node.id} value={node.id}>
                                            {node.name} (剩余: {(node.capacity || 0) - (node.current_count || 0)})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </TabPane>

                        <TabPane tab="配额设置" key="quota">
                            <Form.Item label="磁盘配额 (MB)" name="disk_quota" initialValue={1024}>
                                <InputNumber min={100} max={102400} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="流量配额 (GB/月)" name="bandwidth_quota" initialValue={10}>
                                <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="数据库配额 (MB)" name="db_quota" initialValue={100}>
                                <InputNumber min={0} max={10240} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="数据库类型" name="db_type" initialValue="mysql">
                                <Select>
                                    <Option value="mysql">MySQL</Option>
                                    <Option value="sqlsrv">SQL Server</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="计划任务配额" name="cron_quota" initialValue={5}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="域名绑定数" name="domain_quota" initialValue={-1} extra="-1 表示不限制">
                                <InputNumber min={-1} max={1000} style={{ width: '100%' }} />
                            </Form.Item>
                        </TabPane>

                        <TabPane tab="高级设置" key="advanced">
                            <Form.Item label="最大进程数" name="max_worker" initialValue={0} extra="0 表示不限制">
                                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="最大队列数" name="max_queue" initialValue={0} extra="0 表示不限制">
                                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="FTP 连接数" name="ftp_connect" initialValue={0}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label=".htaccess 支持" name="htaccess_support" valuePropName="checked" initialValue={true}>
                                <Switch />
                            </Form.Item>

                            <Form.Item label="FTP 功能" name="ftp_enabled" valuePropName="checked" initialValue={true}>
                                <Switch />
                            </Form.Item>

                            <Form.Item label="独立日志" name="log_independent" valuePropName="checked" initialValue={false}>
                                <Switch />
                            </Form.Item>

                            <Form.Item label="日志分析" name="log_analysis" valuePropName="checked" initialValue={false}>
                                <Switch />
                            </Form.Item>
                        </TabPane>
                    </Tabs>
                </Form>
            </Modal>

            {/* 配额管理 */}
            <Modal
                title={`配额管理 - ${selectedVhost?.name}`}
                open={quotaModalVisible}
                onOk={handleQuotaSubmit}
                onCancel={() => setQuotaModalVisible(false)}
                width={700}
            >
                <Form form={quotaForm} layout="vertical">
                    <Tabs defaultActiveKey="basic">
                        <TabPane tab="基础配额" key="basic">
                            <Form.Item label="磁盘配额 (MB)" name="disk_quota">
                                <InputNumber min={100} max={102400} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="流量配额 (GB/月)" name="bandwidth_quota">
                                <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="数据库配额 (MB)" name="db_quota">
                                <InputNumber min={0} max={10240} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="计划任务配额" name="cron_quota">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="域名绑定数" name="domain_quota">
                                <InputNumber min={-1} max={1000} style={{ width: '100%' }} />
                            </Form.Item>
                        </TabPane>

                        <TabPane tab="性能限制" key="performance">
                            <Form.Item label="最大进程数" name="max_worker">
                                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="最大队列数" name="max_queue">
                                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="最大连接数" name="max_connect">
                                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="速度限制 (KB/s)" name="speed_limit">
                                <InputNumber min={0} max={102400} style={{ width: '100%' }} />
                            </Form.Item>
                        </TabPane>

                        <TabPane tab="功能开关" key="features">
                            <Form.Item label=".htaccess 支持" name="htaccess_support" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item label="FTP 功能" name="ftp_enabled" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item label="独立日志" name="log_independent" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item label="日志分析" name="log_analysis" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item label="自定义控制" name="custom_control" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item label="SSI 支持" name="ssi_support" valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Form.Item label="忽略备份" name="ignore_backup" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </TabPane>
                    </Tabs>
                </Form>
            </Modal>

            {/* 修改密码 */}
            <Modal
                title={`修改密码 - ${selectedVhost?.name}`}
                open={passwordModalVisible}
                onOk={handlePasswordSubmit}
                onCancel={() => setPasswordModalVisible(false)}
                width={500}
            >
                <Form form={passwordForm} layout="vertical">
                    <Form.Item
                        label="新密码"
                        name="password"
                        rules={[
                            { required: true, message: '请输入新密码' },
                            { min: 6, message: '密码至少6位' }
                        ]}
                    >
                        <Input.Password placeholder="请输入新密码" />
                    </Form.Item>

                    <Form.Item
                        label="确认密码"
                        name="confirm_password"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: '请确认密码' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('两次输入的密码不一致'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="请再次输入密码" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 详情查看 */}
            <Modal
                title={`虚拟主机详情 - ${selectedVhost?.name}`}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        关闭
                    </Button>
                ]}
                width={800}
            >
                {selectedVhost && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedVhost.id}</Descriptions.Item>
                        <Descriptions.Item label="虚拟主机名">{selectedVhost.name}</Descriptions.Item>
                        <Descriptions.Item label="用户ID">{selectedVhost.user_id}</Descriptions.Item>
                        <Descriptions.Item label="节点">{selectedVhost.node_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="状态">
                            {getStatusBadge(selectedVhost.status)}
                        </Descriptions.Item>
                        <Descriptions.Item label="创建时间">{selectedVhost.created_at}</Descriptions.Item>
                        <Descriptions.Item label="磁盘配额">
                            {selectedVhost.quota?.disk_quota} MB
                        </Descriptions.Item>
                        <Descriptions.Item label="流量配额">
                            {selectedVhost.quota?.bandwidth_quota} GB/月
                        </Descriptions.Item>
                        <Descriptions.Item label="数据库配额">
                            {selectedVhost.quota?.db_quota} MB
                        </Descriptions.Item>
                        <Descriptions.Item label="数据库类型">
                            {selectedVhost.quota?.db_type}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* 变更归属 */}
            <Modal
                title={`变更归属 - ${selectedVhost?.name}`}
                open={ownerModalVisible}
                onOk={handleOwnerSubmit}
                onCancel={() => setOwnerModalVisible(false)}
                width={500}
            >
                <Form form={ownerForm} layout="vertical">
                    <Form.Item
                        label="新用户"
                        name="new_user_id"
                        rules={[{ required: true, message: '请选择新用户' }]}
                    >
                        <Select
                            showSearch
                            placeholder="请选择新用户"
                            optionFilterProp="children"
                            onSearch={loadUsers}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {users.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {user.id} - {user.username} ({user.email})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 续期 */}
            <Modal
                title={`续期 - ${selectedVhost?.name}`}
                open={extendModalVisible}
                onOk={handleExtendSubmit}
                onCancel={() => setExtendModalVisible(false)}
                width={500}
            >
                <Form form={extendForm} layout="vertical">
                    <Form.Item
                        label="延长天数"
                        name="days"
                        rules={[{ required: true, message: '请输入延长天数' }]}
                    >
                        <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="免费续期" name="free" valuePropName="checked" extra="开启则不扣费">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 操作日志 */}
            <Drawer
                title={`操作日志 - ${selectedVhost?.name || ''}`}
                open={logsDrawerVisible}
                onClose={() => setLogsDrawerVisible(false)}
                width={720}
            >
                <Table
                    size="small"
                    rowKey={(record, index) => record.id ?? index}
                    loading={logsLoading}
                    dataSource={logsData}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: logsLoading ? ' ' : <Empty description="暂无操作日志" /> }}
                    columns={[
                        { title: '时间', dataIndex: 'time', key: 'time', render: (t, r) => t || r.created_at || r.date || '-' },
                        { title: '操作', dataIndex: 'action', key: 'action', render: (a, r) => a || r.type || r.event || '-' },
                        { title: '详情', dataIndex: 'message', key: 'message', render: (m, r) => m || r.detail || r.content || '-' },
                    ]}
                />
            </Drawer>

            {/* 备份管理 */}
            <Drawer
                title={`备份管理 - ${selectedVhost?.name || ''}`}
                open={backupsDrawerVisible}
                onClose={() => setBackupsDrawerVisible(false)}
                width={720}
                extra={
                    <Space>
                        <Button icon={<CloudUploadOutlined />} onClick={() => handleCreateBackup('web')}>
                            备份网站
                        </Button>
                        <Button icon={<CloudUploadOutlined />} onClick={() => handleCreateBackup('db')}>
                            备份数据库
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => selectedVhost && loadBackups(selectedVhost)}
                        >
                            刷新
                        </Button>
                    </Space>
                }
            >
                <Table
                    size="small"
                    rowKey={(record, index) => record.id ?? record.name ?? index}
                    loading={backupsLoading}
                    dataSource={backupsData}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: backupsLoading ? ' ' : <Empty description="暂无备份" /> }}
                    columns={[
                        { title: '备份名称', dataIndex: 'name', key: 'name', render: (n, r) => n || r.file || r.filename || '-' },
                        { title: '类型', dataIndex: 'type', key: 'type', render: (t) => t || '-' },
                        { title: '大小', dataIndex: 'size', key: 'size', render: (s) => s || '-' },
                        { title: '时间', dataIndex: 'time', key: 'time', render: (t, r) => t || r.created_at || r.date || '-' },
                    ]}
                />
            </Drawer>
        </div>
    );
};

export default Vhosts;
